import type { HtmlTimeMode } from './timeMapping';
import { resolveSeekTarget } from './timeMapping';
import { buildCompositionSrcdoc } from './compositionBootstrap';
import type { CompositionSerialization } from './compositionAgent';

// A stalled load/rasterize must never block the pipeline, so each async step races this timeout.
const STEP_TIMEOUT_MS = 8000;
// A frame that never reports its blank document must not hold a composition back for long.
const FRAME_READY_TIMEOUT_MS = 2000;

async function withTimeout<T>(
    promise: Promise<T>,
    label: string,
    ms = STEP_TIMEOUT_MS,
): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
            () => reject(new Error(`HtmlCompositionRenderer: ${label} timed out after ${ms}ms`)),
            ms,
        );
    });
    try {
        return await Promise.race([promise, timeout]);
    } finally {
        clearTimeout(timer);
    }
}

// The host's CSP nonce, which a nonce-based policy requires on the composition's own inline
// script/style. Absent in the NW.js render worker and in tests.
function hostCspNonce(): string | undefined {
    return typeof window !== 'undefined'
        ? (window as unknown as { _CSP_NONCE?: string })._CSP_NONCE
        : undefined;
}

function randomToken(): string {
    const crypto = globalThis.crypto;
    if (crypto && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `t${Math.floor(Math.random() * 1e16).toString(36)}`;
}

export interface HtmlSeekableTimeline {
    progress(value: number, suppressEvents?: boolean): unknown;
    totalTime?(value: number, suppressEvents?: boolean): unknown;
}

export class HtmlCompositionRenderer {
    #iframe: HTMLIFrameElement | null = null;
    #frameReady: Promise<void> | null = null;
    #inertFrame: HTMLIFrameElement | null = null;
    #readyPromise: Promise<void> | null = null;
    #loadedHtml: string | null = null;
    #ready = false;
    #width: number;
    #height: number;
    #destroyed = false;
    #timeMode: HtmlTimeMode = 'normalized';
    #sceneDurationMs = 0;
    #token = randomToken();
    #nextRequestId = 0;
    #pending = new Map<number, { resolve(value: unknown): void; reject(error: Error): void }>();
    #generation = 0;
    #target: Window | null = null;
    #resolveReady: (() => void) | null = null;

    constructor(width: number, height: number) {
        this.#width = width;
        this.#height = height;
        if (typeof window !== 'undefined') {
            window.addEventListener('message', this.#onMessage);
        }
    }

    #onMessage = (event: MessageEvent): void => {
        const data = event.data as {
            l5?: number;
            token?: string;
            event?: string;
            id?: number;
            ok?: boolean;
            result?: unknown;
            error?: string;
        } | null;
        if (!data || data.l5 !== 1 || data.token !== this.#token) {
            return;
        }
        if (!this.#iframe || event.source !== this.#iframe.contentWindow) {
            return;
        }
        if (data.event === 'ready') {
            this.#target = event.source as Window;
            this.#resolveReady?.();
            return;
        }
        this.#settle(data);
    };

    get isReady(): boolean {
        return this.#ready;
    }

    get _agentToken(): string {
        return this.#token;
    }

    setSize(width: number, height: number): void {
        if (this.#width === width && this.#height === height) {
            return;
        }
        this.#width = width;
        this.#height = height;
        for (const frame of [this.#iframe, this.#inertFrame]) {
            if (frame) {
                frame.style.width = `${width}px`;
                frame.style.height = `${height}px`;
                frame.width = String(width);
                frame.height = String(height);
            }
        }
    }

    async load(html: string): Promise<void> {
        if (this.#loadedHtml === html && this.#readyPromise !== null) {
            return this.#readyPromise;
        }
        this.#loadedHtml = html;
        this.#ready = false;
        this.#failPending(new Error('HtmlCompositionRenderer: composition reloaded'));
        this.#generation += 1;
        this.#token = randomToken();
        this.#ensureIframe();

        const iframe = this.#iframe;
        if (!iframe) {
            this.#readyPromise = Promise.resolve();
            return this.#readyPromise;
        }

        const generation = this.#generation;
        this.#target = null;
        // Build the srcdoc before arming the ready timeout: the first composition pulls the vendored
        // gsap chunk, and that load must not eat into the agent-ready budget.
        const srcdoc = await buildCompositionSrcdoc(html, this.#token, hostCspNonce());
        await this.#frameReady;
        const readyPromise = new Promise<void>((resolve) => {
            this.#resolveReady = () => {
                if (generation === this.#generation) {
                    this.#ready = true;
                }
                resolve();
            };
            const onLoad = () => {
                if (!iframe.contentWindow || generation !== this.#generation) {
                    resolve();
                }
            };
            iframe.addEventListener('load', onLoad, { once: true });
            iframe.srcdoc = srcdoc;
        });
        const ready = withTimeout(readyPromise, 'composition agent ready');
        this.#readyPromise = ready;
        // A failed load must not be cached as the state of this html: the memo above would hand the
        // same rejection to every later call and leave the asset permanently unusable. Forget it, so
        // the next load genuinely retries. The `catch` also keeps this branch from surfacing as an
        // unhandled rejection when the caller has already given up.
        ready.catch(() => {
            if (this.#readyPromise === ready) {
                this.#readyPromise = null;
                this.#loadedHtml = null;
            }
        });
        return ready;
    }

    setTimeMapping(mode: HtmlTimeMode, sceneDurationMs: number): void {
        this.#timeMode = mode;
        this.#sceneDurationMs = sceneDurationMs;
    }

    /** Seek the composition's timeline for a normalized scene progress. No-op with no timeline. */
    async seek(progress: number): Promise<void> {
        const target = resolveSeekTarget(this.#timeMode, progress, this.#sceneDurationMs);
        await this.#request('seek', {
            mode: target.kind,
            seconds: target.kind === 'totalTime' ? target.value : 0,
            progress: Math.max(0, Math.min(1, progress)),
        });
    }

    async rasterize(scale: number): Promise<ImageBitmap> {
        const bitmap = await this.#request('rasterize', {
            width: this.#width,
            height: this.#height,
            scale,
        });
        if (!(bitmap instanceof ImageBitmap)) {
            throw new Error('HtmlCompositionRenderer: the composition returned no raster');
        }
        return bitmap;
    }

    async introspectionDocument(): Promise<Document | null> {
        const serialized = (await this.#request(
            'serialize',
            {},
        )) as CompositionSerialization | null;
        if (!serialized || typeof serialized.html !== 'string') {
            // Null only where there is no composition frame to ask — the render worker, and tests
            // with no DOM. Where there is one and it did not answer, say so: a caller that reads
            // this as "nothing to report" would treat an unmeasured composition as a clean one.
            // Kept to introspection on purpose. `seek` and the node edits are fire-and-forget, and
            // the render pipeline tolerates them going nowhere; throwing there would take a whole
            // render down over a composition that is merely unreachable for the moment.
            if (this.#iframe) {
                throw new Error(
                    'HtmlCompositionRenderer: the composition did not answer, so there is no copy ' +
                        'to measure',
                );
            }
            return null;
        }
        const frame = this.#ensureInertFrame();
        if (!frame) {
            return null;
        }
        await withTimeout(
            new Promise<void>((resolve) => {
                frame.addEventListener('load', () => resolve(), { once: true });
                frame.srcdoc = serialized.html;
            }),
            'introspection copy load',
        );
        const document_ = frame.contentDocument;
        if (!document_) {
            // The copy loaded, so it has a document; null here is a fault rather than the absence of
            // a composition, and a caller reading it as the latter would measure nothing and call
            // the composition clean.
            throw new Error(
                'HtmlCompositionRenderer: the introspection copy loaded but exposed no document',
            );
        }
        if (document_.fonts) {
            await withTimeout(document_.fonts.ready, 'introspection copy fonts');
        }
        this.#assertCopyFidelity(document_, serialized);
        return document_;
    }

    /**
     * The introspection copy as it currently stands, or null before one has been taken. Live rather
     * than a handle from an earlier call: one inert frame serves every pose, so re-posing detaches
     * the document a caller was handed and leaves it measuring zero.
     */
    get currentIntrospectionDocument(): Document | null {
        return this.#inertFrame?.contentDocument ?? null;
    }

    /** Drop the introspection copy; the next `introspectionDocument` builds a fresh frame. */
    releaseIntrospectionCopy(): void {
        this.#inertFrame?.parentNode?.removeChild(this.#inertFrame);
        this.#inertFrame = null;
    }

    async timelineInfo(): Promise<{
        time: number;
        duration: number;
        measurable: boolean;
    } | null> {
        return (await this.#request('timelineInfo', {})) as {
            time: number;
            duration: number;
            measurable: boolean;
        } | null;
    }

    async setNodeText(id: string, html: string): Promise<void> {
        await this.#request('setNodeText', { id, html });
    }

    async setNodeStyle(id: string, styles: Record<string, string>): Promise<void> {
        await this.#request('setNodeStyle', { id, styles });
    }

    destroy(): void {
        this.#destroyed = true;
        this.#ready = false;
        this.#readyPromise = null;
        this.#loadedHtml = null;
        this.#failPending(new Error('HtmlCompositionRenderer: destroyed'));
        this.#target = null;
        this.#resolveReady = null;
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.#onMessage);
        }
        for (const frame of [this.#iframe, this.#inertFrame]) {
            frame?.parentNode?.removeChild(frame);
        }
        this.#iframe = null;
        this.#frameReady = null;
        this.#inertFrame = null;
    }

    #assertCopyFidelity(copy: Document, serialized: CompositionSerialization): void {
        if (serialized.shadowHosts > 0) {
            throw new Error(
                `HtmlCompositionRenderer: the composition has ${serialized.shadowHosts} element(s) ` +
                    `with a shadow root, whose content no serialization preserves, so measurements ` +
                    `of this copy would silently omit it.`,
            );
        }
        // Counted from `documentElement`, matching how the composition counted what it serialized:
        // an element is not part of its own descendant list, but a document's count includes its
        // `<html>`, and comparing the two would be off by exactly that one.
        const actual = copy.documentElement?.getElementsByTagName('*').length ?? -1;
        if (actual !== serialized.elementCount) {
            throw new Error(
                `HtmlCompositionRenderer: the introspection copy has ${actual} elements but the ` +
                    `composition serialized ${serialized.elementCount}. Something in it does not ` +
                    `survive a parse round-trip, so measurements of this copy would not describe ` +
                    `the composition.`,
            );
        }
    }

    #failPending(error: Error): void {
        for (const pending of this.#pending.values()) {
            pending.reject(error);
        }
        this.#pending.clear();
    }

    async #request(op: string, args: Record<string, unknown>): Promise<unknown> {
        if (!this.#iframe) {
            return null;
        }
        if (this.#readyPromise) {
            await this.#readyPromise;
        }
        const target = this.#target;
        if (!target) {
            return null;
        }
        const id = this.#nextRequestId++;
        const generation = this.#generation;
        const answer = new Promise<unknown>((resolve, reject) => {
            this.#pending.set(id, {
                resolve: (value) => {
                    if (generation === this.#generation) {
                        resolve(value);
                    } else {
                        reject(new Error('HtmlCompositionRenderer: stale composition generation'));
                    }
                },
                reject,
            });
            // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration
            target.postMessage({ l5: 1, token: this.#token, id, op, args }, '*');
        });
        try {
            return await withTimeout(answer, `composition ${op}`);
        } finally {
            this.#pending.delete(id);
        }
    }

    #settle(data: { id?: number; ok?: boolean; result?: unknown; error?: string }): void {
        if (typeof data.id !== 'number') {
            return;
        }
        const pending = this.#pending.get(data.id);
        if (!pending) {
            return;
        }
        this.#pending.delete(data.id);
        if (data.ok) {
            pending.resolve(data.result);
        } else {
            pending.reject(
                new Error(`HtmlCompositionRenderer: composition reported "${data.error}"`),
            );
        }
    }

    #styleFrame(iframe: HTMLIFrameElement): void {
        iframe.setAttribute('aria-hidden', 'true');
        iframe.setAttribute('tabindex', '-1');
        // Off-screen but still laid out and rendered: `display:none` would zero the document's
        iframe.style.position = 'fixed';
        iframe.style.top = '0';
        iframe.style.left = '-99999px';
        iframe.style.border = '0';
        iframe.style.pointerEvents = 'none';
        iframe.style.width = `${this.#width}px`;
        iframe.style.height = `${this.#height}px`;
        iframe.width = String(this.#width);
        iframe.height = String(this.#height);
    }

    #ensureIframe(): void {
        if (this.#iframe || this.#destroyed || typeof document === 'undefined') {
            return;
        }
        const iframe = document.createElement('iframe');
        iframe.setAttribute('sandbox', 'allow-scripts');
        iframe.setAttribute('data-l5-composition', '');
        this.#styleFrame(iframe);
        document.body.appendChild(iframe);
        this.#iframe = iframe;
        // The frame is sandboxed to an opaque origin, so it is its own renderer process and is told
        // its viewport size asynchronously. A composition loaded before that arrives parses into a
        // document with no layout — every getBoundingClientRect() in it reads 0, `document.body`
        // included — so one that sizes its motion from a measurement animates by zero and renders
        // frozen, throwing nothing. Loading about:blank first and waiting for it is what gets the
        // size committed; it is paid once per renderer, since the frame is reused across loads.
        this.#frameReady = new Promise<void>((resolve) => {
            const settle = (): void => resolve();
            iframe.addEventListener('load', settle, { once: true });
            iframe.src = 'about:blank';
            setTimeout(settle, FRAME_READY_TIMEOUT_MS);
        });
    }

    #ensureInertFrame(): HTMLIFrameElement | null {
        if (this.#destroyed || typeof document === 'undefined') {
            return null;
        }
        if (this.#inertFrame) {
            return this.#inertFrame;
        }
        const iframe = document.createElement('iframe');
        iframe.setAttribute('sandbox', 'allow-same-origin');
        iframe.setAttribute('data-l5-composition-copy', '');
        this.#styleFrame(iframe);
        document.body.appendChild(iframe);
        this.#inertFrame = iframe;
        return iframe;
    }
}
